import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getTemplateById } from "../services/templateService";
import { createLegalEntity, listLegalEntities } from "../services/legalEntityService";
import { createRepresentative, listRepresentatives } from "../services/representativeService";
import { listActiveBusinessUnits } from "../services/businessUnitService";
import type { BusinessUnitOption } from "../types/businessUnit";
import { mergeBusinessUnitOptionsFromApi } from "../utils/businessUnitApiCodes";
import type { Template, ContractReviewForm } from "../types/template";
import type { CreateLegalEntityInput, LegalEntity } from "../types/legalEntity";
import type { CreateRepresentativeInput, Representative } from "../types/representative";
import { ContractReviewDetailsView } from "../components/organisms/ContractReviewDetailsView";
import type { GeneratedContractDraft } from "../services/generatedContractService";
import { commitGeneratedContract } from "../services/generatedContractService";
import { runPostCommitSharePointUpload } from "../services/graphService";

export const ReviewTemplatePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [template, setTemplate] = useState<Template | null>(null);
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([]);
  const [legalEntitiesError, setLegalEntitiesError] = useState<string | null>(null);
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [representativesError, setRepresentativesError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState<GeneratedContractDraft | null>(null);
  const [capturedDocumentName, setCapturedDocumentName] = useState<string | null>(null);
  const [businessUnitRows, setBusinessUnitRows] = useState<readonly BusinessUnitOption[]>([]);

  const refreshLegalEntities = async (): Promise<LegalEntity[]> => {
    const entities = await listLegalEntities();
    setLegalEntities(entities);
    return entities;
  };

  const refreshRepresentatives = async (): Promise<Representative[]> => {
    const rows = await listRepresentatives();
    setRepresentatives(rows);
    return rows;
  };

  useEffect(() => {
    const state = location.state as
      | { draft?: GeneratedContractDraft; capturedDocumentName?: string }
      | undefined;
    if (state?.draft) {
      setDraft(state.draft);
    }
    const name = (state?.capturedDocumentName ?? "").trim();
    setCapturedDocumentName(name !== "" ? name : null);

    if (id) {
      Promise.all([
        getTemplateById(id),
        listActiveBusinessUnits().catch(() => [] as BusinessUnitOption[]),
        refreshLegalEntities().catch(() => {
          setLegalEntitiesError("Unable to load legal entities");
          return [] as LegalEntity[];
        }),
        refreshRepresentatives().catch(() => {
          setRepresentativesError("Unable to load representatives");
          return [] as Representative[];
        })
      ]).then(([t, buOptions, entities, representativeRows]) => {
        setTemplate(t ?? null);
        setBusinessUnitRows(mergeBusinessUnitOptionsFromApi(buOptions));
        setLegalEntities(entities);
        setRepresentatives(representativeRows);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [id, location.state]);

  const handleBack = () => {
    navigate("/contractiq/templates");
  };

  const handleGenerateContract = async (form: ContractReviewForm) => {
    if (!id || !template || !draft) {
      return;
    }

    const commitJson = await commitGeneratedContract(draft.generatedContractId, {
      display_name: form.contractName,
      form_answers_json: {
        contractName: form.contractName,
        templateId: form.templateId,
        contractType: form.contractType,
        businessUnit: form.businessUnit,
        contractOrigination: form.contractOrigination,
        effectiveDate: form.effectiveDate,
        legalEntityName: form.legalEntityName,
        contactName: form.contactName,
        contactTitle: form.contactTitle,
        representativeId: form.representativeId,
        representativeName: form.representativeName,
        representativeTitle: form.representativeTitle,
        representativeCompanyName: form.representativeCompanyName,
        representativeAddress: form.representativeAddress,
        representativeStreetAddress: form.representativeStreetAddress,
        representativeCityStateZip: form.representativeCityStateZip,
        representativePhone: form.representativePhone,
        representativeEmail: form.representativeEmail,
        planGroupName: form.planGroupName,
        addressMerged: form.addressMerged,
        companyStreetAddress: form.companyStreetAddress,
        companyCityStateZip: form.companyCityStateZip,
        phone: form.phone,
        email: form.email,
        legalEntityId: form.legalEntityId
      },
      business_unit: form.businessUnit
    });

    if (commitJson.success === false) {
      throw new Error(
        typeof commitJson.message === "string" && commitJson.message.trim()
          ? commitJson.message.trim()
          : "Unable to commit contract."
      );
    }

    void runPostCommitSharePointUpload({
      graphDriveId: commitJson.data.graph_drive_id ?? null,
      graphItemId: commitJson.data.graph_item_id ?? null
    });

    navigate(`/contractiq/templates/${id}/send`, {
      state: {
        template,
        contractReviewForm: form,
        generatedContractId: draft.generatedContractId,
        postCommitGraphBannerPoll: true
      }
    });
  };

  if (isLoading) {
    return <p className="text-muted-foreground">Loading...</p>;
  }

  if (!id || !template) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Template not found</p>
        <button
          type="button"
          onClick={handleBack}
          className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Back to Template Library
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ContractReviewDetailsView
        template={template}
        legalEntities={legalEntities}
        representatives={representatives}
        businessUnitRows={businessUnitRows}
        legalEntitiesError={legalEntitiesError}
        representativesError={representativesError}
        lockedContractName={capturedDocumentName}
        onCreateLegalEntity={async (input: CreateLegalEntityInput) => createLegalEntity(input)}
        onLegalEntitiesRefresh={refreshLegalEntities}
        onCreateRepresentative={async (input: CreateRepresentativeInput) => createRepresentative(input)}
        onRepresentativesRefresh={refreshRepresentatives}
        onGenerateContract={handleGenerateContract}
      />
    </div>
  );
};
